import { createInitialPermissionGovernance } from '../permission-center-ui/DisposablePermissionPreview.fixture';
import { createBusinessModeCatalog } from '../state/businessModes';
import {
  departmentCompanyMappingId,
  disposableCompanyId,
  disposableDepartmentId,
} from '../state/departmentCompanyMapping';
import { createDeviceImageAsset } from '../state/deviceMedia';
import { systemCalculationByRef } from '../state/fieldCalculations';
import { createShareFieldCombinations } from '../state/shareExportFieldCombinations';
import type {
  BusinessCatalog,
  CpqRole,
  CustomerCatalogRecord,
  DepartmentCompanyMappingRecord,
  DeviceCatalogRecord,
  OptionCatalogRecord,
  OptionCategoryRecord,
  PackageCatalogRecord,
  PlanCatalogRecord,
  QuoteFieldCombinationRecord,
  QuoteFieldDefinitionRecord,
  QuoteFieldPoolRecord,
  QuoteFieldPresetRecord,
  RoleCatalogRecord,
  SettingsFieldRecord,
  UserCatalogRecord,
  WarehouseRecord,
} from '../state/types';
import { FIXED_QUOTE_HEADER_FIELD_KEYS } from '../state/unifiedFieldRegistry';
import { devices as legacyDevices } from './catalog';

export const PRODUCT_WAREHOUSES: WarehouseRecord[] = [
  { key: 'sf_rental_xm', name: '租赁-顺丰-厦门' },
  { key: 'sf_rental_cd', name: '租赁-顺丰-成都' },
  { key: 'sf_rental_gy', name: '租赁-顺丰-贵阳' },
  { key: 'sf_rental_cs', name: '租赁-顺丰-长沙' },
];

export interface CustomFieldDefinition {
  key: string;
  label: string;
  type:
    | 'text'
    | 'number'
    | 'money'
    | 'percent'
    | 'code'
    | 'date'
    | 'single-select'
    | 'multi-select'
    | 'boolean';
  width: 'normal' | 'long';
  note?: string;
  options?: Array<{ value: string; label: string }>;
}

export interface CustomFieldGroup {
  key: string;
  label: string;
  fields: CustomFieldDefinition[];
}

/** The business-approved quote fields. They are always selected for a
 * new quote and cannot be removed from an individual quote. */
export const MANDATORY_QUOTE_FIELD_KEYS = [
  'device_image',
  'device_name',
  'quantity',
  'warehouse_stock',
  'device_attribute',
  'rental_price',
  'tax_inclusive_plan_price',
  'rental_subtotal',
  'device_unit_price',
  'unit_price_subtotal',
  'device_compensation_price',
  'scheme_discount',
  'scheme_product_price',
  'scheme_product_subtotal',
  'package_item_price',
  'package_item_monthly_subtotal',
  'item_discount',
  'package_discounted_unit_price',
  'package_discounted_subtotal',
  'product_final_discount_price',
  'retail_price_tax13',
  'purchase_final_price_tax13',
  'purchase_final_price_ex_tax13',
  'dynamic_cost',
  'dynamic_cost_subtotal',
  'device_sku',
  'replacement_device',
  'quote_remark',
  'jky_code',
] as const;

/** Explicit one-time migration deny-list. These identities are not valid
 * custom fields and must never be rehydrated from older local Mock state. */
export const REMOVED_QUOTE_FIELD_KEYS = [
  'original_subtotal',
  'package_final_discount_price',
  'untaxed_scheme_price',
  'purchase_final_price_no_tax_tax13',
  'buyout_tax_1',
  'buyout_tax_2',
  'monthly_buyout_tax',
  'package_original_price_tax13',
  // Replaced by the scoped scheme_product_subtotal projection.
  'scheme_package_price',
] as const;

/** Compatibility export for quote creation. It now points at the approved
 * mandatory registry rather than a separate three-field list. */
export const DEFAULT_CUSTOM_FIELD_KEYS: string[] = [
  ...MANDATORY_QUOTE_FIELD_KEYS,
];

/** Quote-pool fields that are locked for every quote. The approved business
 * list already contains the four structural quote-line identities, so this
 * registry must not append a second copy of them. */
export const MANDATORY_QUOTE_POOL_FIELD_KEYS = [
  ...MANDATORY_QUOTE_FIELD_KEYS,
] as const;

export const DEFAULT_VISIBLE_SYSTEM_FIELD_KEYS = [
  ...MANDATORY_QUOTE_POOL_FIELD_KEYS,
] as const;

/**
 * The seed membership of the quote display pool is explicit.  The unified
 * field registry remains the definition catalogue, but it is not an implicit
 * quote-pool membership source.  Optional system definitions enter the pool
 * only through the administrator's explicit pool action.
 */
export const DEFAULT_QUOTE_FIELD_POOL_KEYS = [
  ...DEFAULT_VISIBLE_SYSTEM_FIELD_KEYS,
] as const;

export const CUSTOM_FIELD_PRESETS = {
  daily: DEFAULT_CUSTOM_FIELD_KEYS,
  package: ['package_item_price', 'scheme_product_subtotal'],
  cost: [
    'dynamic_cost',
    'dynamic_cost_subtotal',
    'purchase_final_price_tax13',
    'valid_until',
  ],
};

export const CUSTOM_FIELD_GROUPS: CustomFieldGroup[] = [
  {
    key: 'basic',
    label: '报价清单必备字段',
    fields: [
      { key: 'jky_code', label: '吉客云编号', type: 'code', width: 'normal' },
      { key: 'rental_price', label: '租金', type: 'money', width: 'normal' },
      {
        key: 'tax_inclusive_plan_price',
        label: '最终买断价',
        type: 'money',
        width: 'normal',
      },
      {
        key: 'device_unit_price',
        label: '单价',
        type: 'money',
        width: 'normal',
      },
      {
        key: 'unit_price_subtotal',
        label: '小计单价/月',
        type: 'money',
        width: 'long',
      },
      {
        key: 'device_compensation_price',
        label: '赔付价格',
        type: 'money',
        width: 'normal',
      },
      {
        key: 'rental_subtotal',
        label: '小计租金（元/月）',
        type: 'money',
        width: 'long',
      },
      {
        key: 'package_item_price',
        label: '套餐价/月',
        type: 'money',
        width: 'long',
      },
      {
        key: 'package_item_monthly_subtotal',
        label: '套餐价小计/月',
        type: 'money',
        width: 'long',
      },
      {
        key: 'scheme_discount',
        label: '方案专属折扣',
        type: 'percent',
        width: 'normal',
      },
      {
        key: 'scheme_product_price',
        label: '方案价/月',
        type: 'money',
        width: 'long',
      },
      {
        key: 'scheme_product_subtotal',
        label: '方案价小计/月',
        type: 'money',
        width: 'long',
      },
      { key: 'item_discount', label: '折扣', type: 'percent', width: 'normal' },
      {
        key: 'product_final_discount_price',
        label: '产品最终折后价',
        type: 'money',
        width: 'long',
      },
      {
        key: 'package_discounted_unit_price',
        label: '套餐最终折后价',
        type: 'money',
        width: 'normal',
      },
      {
        key: 'package_discounted_subtotal',
        label: '套餐最终折后价小计',
        type: 'money',
        width: 'long',
      },
      {
        key: 'retail_price_tax13',
        label: '零售价（含13%专票增值税）',
        type: 'money',
        width: 'long',
      },
      {
        key: 'purchase_final_price_tax13',
        label: '购买最终价（含13%专票增值税）',
        type: 'money',
        width: 'long',
      },
      {
        key: 'purchase_final_price_ex_tax13',
        label: '购买最终价（不含13%专票增值税）',
        type: 'money',
        width: 'long',
      },
      {
        key: 'dynamic_cost',
        label: '动态成本',
        type: 'money',
        width: 'normal',
      },
      {
        key: 'dynamic_cost_subtotal',
        label: '动态成本小计',
        type: 'money',
        width: 'normal',
      },
    ],
  },
  {
    key: 'quote-core',
    label: '报价清单基础字段',
    fields: [
      { key: 'device_image', label: '设备图片', type: 'text', width: 'normal' },
      { key: 'device_name', label: '设备名称', type: 'text', width: 'long' },
      { key: 'device_sku', label: 'SKU', type: 'code', width: 'normal' },
      { key: 'quantity', label: '数量', type: 'number', width: 'normal' },
      { key: 'quote_remark', label: '备注', type: 'text', width: 'long' },
      {
        key: 'warehouse_stock',
        label: '分仓库存',
        type: 'text',
        width: 'long',
      },
      {
        key: 'replacement_device',
        label: '替代设备',
        type: 'text',
        width: 'long',
      },
    ],
  },
  {
    key: 'device-optional',
    label: '设备标准字段',
    fields: [
      {
        key: 'device_category',
        label: '设备分类',
        type: 'text',
        width: 'normal',
      },
      {
        key: 'device_attribute',
        label: '设备属性',
        type: 'single-select',
        width: 'normal',
        options: ['租赁用', '销售用', '通用类'].map((value) => ({
          value,
          label: value,
        })),
      },
      {
        key: 'procurement_attribute',
        label: '采购属性',
        type: 'single-select',
        width: 'normal',
        options: ['可以采购', '不可采购', '暂停采购'].map((value) => ({
          value,
          label: value,
        })),
      },
      {
        key: 'device_weight',
        label: '重量（kg）',
        type: 'number',
        width: 'normal',
      },
    ],
  },
  {
    key: 'quote-header',
    label: '报价抬头',
    fields: [
      { key: 'quote_title', label: '报价标题', type: 'text', width: 'long' },
      { key: 'customer_name', label: '客户名称', type: 'text', width: 'long' },
      {
        key: 'customer_contact',
        label: '联系人',
        type: 'text',
        width: 'normal',
      },
      {
        key: 'customer_phone',
        label: '联系电话',
        type: 'text',
        width: 'normal',
      },
      { key: 'valid_until', label: '有效期', type: 'date', width: 'normal' },
      {
        key: 'business_mode',
        label: '业务模式',
        type: 'single-select',
        width: 'normal',
      },
      { key: 'deposit', label: '押金', type: 'money', width: 'normal' },
      {
        key: 'overall_discount',
        label: '整单折扣',
        type: 'percent',
        width: 'normal',
      },
    ],
  },
];

const APPROVED_CALCULATION_REFS: Record<string, string> = {
  rental_subtotal: 'quote-rental-subtotal-v1',
  package_item_monthly_subtotal: 'package-item-monthly-subtotal-v1',
  product_final_discount_price: 'final-discounted-relation-line-v2',
  scheme_product_price: 'plan-product-price-v2',
  scheme_product_subtotal: 'plan-product-subtotal-v2',
  package_discount_quote_tax13: 'package-discounted-quote-v2',
  package_discounted_unit_price: 'package-discounted-unit-price-v2',
  package_discounted_subtotal: 'package-discounted-subtotal-v2',
  dynamic_cost_subtotal: 'dynamic-cost-subtotal-v1',
};

export function createQuoteFieldDefinitions(): QuoteFieldDefinitionRecord[] {
  let sortOrder = 0;
  const baseDefinitions = CUSTOM_FIELD_GROUPS.flatMap((group) =>
    group.fields.map((field) => {
      sortOrder += 1;
      const approvedCalculationRef = APPROVED_CALCULATION_REFS[field.key];
      const calculated = Boolean(approvedCalculationRef);
      const calculation = approvedCalculationRef
        ? systemCalculationByRef(approvedCalculationRef)
        : undefined;
      const required = MANDATORY_QUOTE_POOL_FIELD_KEYS.includes(
        field.key as (typeof MANDATORY_QUOTE_POOL_FIELD_KEYS)[number],
      );
      return {
        ...field,
        groupKey: group.key,
        groupLabel: group.label,
        sortOrder,
        enabled: true,
        defaultVisible: DEFAULT_VISIBLE_SYSTEM_FIELD_KEYS.includes(
          field.key as (typeof DEFAULT_VISIBLE_SYSTEM_FIELD_KEYS)[number],
        ),
        sourceKind: 'system' as const,
        systemLocked: true,
        ...(calculation ? { calculation } : {}),
        governance: {
          fieldKey: field.key,
          kind: calculated
            ? ('system-calculated' as const)
            : ('system-fixed' as const),
          classification: 'ordinary' as const,
          required,
          definitionVersion: 'definition-v2',
          lockedAttributes: [
            'fieldKey',
            'kind',
            'classification',
            'enabled',
            'required',
            'defaultVisible',
          ],
          ...(calculated
            ? {
                formulaRef: approvedCalculationRef,
                // Keep the first-stage calculation-v2 governance marker while
                // the structured definition carries its own version.
                formulaVersion: 'calculation-v2',
              }
            : {}),
        },
      };
    }),
  );
  return baseDefinitions;
}

export function createQuoteFieldPresets(): QuoteFieldPresetRecord[] {
  return [
    {
      id: 'daily',
      name: '日常报价预设',
      fieldKeys: [...DEFAULT_CUSTOM_FIELD_KEYS],
      enabled: true,
      isDefault: true,
      systemLocked: true,
      sortOrder: 1,
    },
    {
      id: 'package',
      name: '套餐核价预设',
      fieldKeys: [...CUSTOM_FIELD_PRESETS.package],
      enabled: true,
      isDefault: false,
      systemLocked: true,
      sortOrder: 2,
    },
    {
      id: 'cost',
      name: '成本买断预设',
      fieldKeys: [...CUSTOM_FIELD_PRESETS.cost],
      enabled: true,
      isDefault: false,
      systemLocked: true,
      sortOrder: 3,
    },
  ];
}

/** Separate display pool: definitions and display presets must not become the
 * same resource in the Disposable Mock. */
export function createQuoteFieldPool(): QuoteFieldPoolRecord[] {
  const summaryFields = new Set<string>();
  const headerFields = new Set<string>(FIXED_QUOTE_HEADER_FIELD_KEYS);
  const defaultPoolKeys = new Set<string>(DEFAULT_QUOTE_FIELD_POOL_KEYS);
  const poolOrder = new Map<string, number>(
    DEFAULT_QUOTE_FIELD_POOL_KEYS.map((fieldKey, index) => [fieldKey, index]),
  );
  return createQuoteFieldDefinitions()
    .filter(
      (field) => defaultPoolKeys.has(field.key) && !headerFields.has(field.key),
    )
    .map((field) => ({
      id: `pool-${field.key.replaceAll('_', '-')}`,
      fieldKey: field.key,
      displayLabel: field.label,
      placement: summaryFields.has(field.key)
        ? ('package-summary' as const)
        : ('quote-line' as const),
      snapshotPolicy: 'freeze-on-select' as const,
      enabled: true,
      mandatory: field.governance.required,
      defaultVisible: field.defaultVisible,
      sortOrder: field.sortOrder,
    }))
    .sort(
      (left, right) =>
        (poolOrder.get(left.fieldKey) ?? Number.MAX_SAFE_INTEGER) -
        (poolOrder.get(right.fieldKey) ?? Number.MAX_SAFE_INTEGER),
    )
    .map((entry, index) => ({ ...entry, sortOrder: index + 1 }));
}

export function createQuoteFieldCombinations(): QuoteFieldCombinationRecord[] {
  return [
    {
      id: 'combination-daily',
      name: '日常报价组合',
      fieldKeys: [
        'device_unit_price',
        'product_final_discount_price',
        'jky_code',
      ],
      scope: '全公司',
      isDefault: true,
      enabled: true,
      systemLocked: true,
      sortOrder: 1,
    },
    {
      id: 'combination-cost',
      name: '成本思路组合（待规则输入）',
      fieldKeys: ['item_discount'],
      scope: '管理层',
      isDefault: false,
      enabled: false,
      systemLocked: true,
      sortOrder: 2,
    },
  ];
}

const PRODUCT_NAMES = [
  ['摄像机', 'Sony ZV-E10 II'],
  ['摄像机', 'Sony A7M4'],
  ['摄像机', '松下 GH6'],
  ['摄像机', '大疆 Pocket 3'],
  ['摄像机', '索尼 FX3'],
  ['摄像机', '佳能 C70'],
  ['镜头', 'Sony 16-35 PZ'],
  ['镜头', 'Sony 24-70 GM II'],
  ['镜头', '腾龙 28-75 G2'],
  ['镜头', '小痰盂 50mm'],
  ['镜头', '适马 18-35mm'],
  ['镜头', '适马 85mm'],
  ['灯光', '爱图仕 300X'],
  ['灯光', '锐鹰 RX-18TDX'],
  ['灯光', '爱图仕 600D Pro'],
  ['灯光', '南光 Forza 60B'],
  ['灯光', '神牛 AD400'],
  ['灯光', '南光 FS-150D'],
  ['收音', '大疆 Mic 2'],
  ['收音', 'RODE Wireless Pro'],
  ['收音', '森海塞尔 MKE600'],
  ['收音', '无线领夹麦套装'],
  ['收音', '枪式麦克风套装'],
  ['收音', '八爪鱼录音套装'],
  ['采集与导播', 'ATEM Mini Extreme'],
  ['采集与导播', 'ATEM Mini Extreme ISO'],
  ['采集与导播', '圆刚 GC553G2'],
  ['采集与导播', '切换台 V-60HD'],
  ['采集与导播', '编码器一体机'],
  ['采集与导播', '多机位导播套装'],
  ['电脑与配件', 'MacBook Pro M3'],
  ['电脑与配件', 'MacBook Pro 16'],
  ['电脑与配件', '碳纤维三脚架'],
  ['电脑与配件', '稳定器如影 S'],
  ['电脑与配件', '读卡器套装'],
  ['电脑与配件', '移动硬盘 2T'],
] as const;

const DEVICE_RETAIL_PRICE_SEED_BY_ID: Record<string, number> = {
  'DEV-001': 5700,
  'DEV-002': 3950,
  'DEV-003': 5700,
  'DEV-004': 16000,
  'DEV-005': 1200,
  'DEV-006': 1800,
  'DEV-007': 9600,
  'DEV-008': 6100,
  'DEV-009': 11350,
  'DEV-010': 2700,
  'DEV-011': 3000,
  'DEV-012': 15600,
  'DEV-013': 11750,
  'DEV-014': 18750,
  'DEV-015': 10000,
  'DEV-016': 15250,
  'DEV-019': 13900,
  'DEV-020': 15650,
  'DEV-025': 17800,
  'DEV-026': 17800,
  'DEV-027': 21300,
  'DEV-031': 21700,
};

const productImage = (name: string, index: number) => {
  const colors = [
    '#e6f4ff',
    '#f6ffed',
    '#fff7e6',
    '#fff1f0',
    '#f9f0ff',
    '#e6fffb',
  ];
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80"><rect width="80" height="80" rx="12" fill="${colors[index % colors.length]}"/><circle cx="40" cy="35" r="16" fill="#722ed1" opacity=".16"/><text x="40" y="62" font-size="9" text-anchor="middle" fill="#262626">${name.slice(0, 8)}</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

function productSku(category: string, index: number) {
  return `SKU-${category.slice(0, 1)}${String(index + 1).padStart(4, '0')}`;
}

function buildDevices(): DeviceCatalogRecord[] {
  return PRODUCT_NAMES.map(([category, name], index) => {
    const deviceId = `DEV-${String(index + 1).padStart(3, '0')}`;
    const legacy = legacyDevices.find((device) => device.name === name);
    const retailPriceTax13 =
      DEVICE_RETAIL_PRICE_SEED_BY_ID[deviceId] ??
      (((index * 37) % 196) + 5) * 100;
    const rentalPrice =
      legacy?.rentalPrice ?? [285, 198, 25, 435, 910, 900][index % 6];
    const unitPrice = [520, 460, 180, 780, 1250, 1180][index % 6] + index * 7;
    const untaxedPlanPrice =
      [480, 420, 160, 720, 1160, 1090][index % 6] + index * 11;
    const taxInclusivePlanPrice =
      [542, 475, 181, 814, 1311, 1232][index % 6] + index * 13;
    const compensationPrice =
      [6800, 5200, 900, 9600, 14800, 13200][index % 6] + index * 19;
    const buyoutFinalPriceTax13 =
      DEVICE_RETAIL_PRICE_SEED_BY_ID[deviceId] ??
      (((index * 37) % 196) + 5) * 100;
    const dynamicCost = [3200, 2400, 360, 5100, 10900, 10800][index % 6];
    const stockByWarehouse = Object.fromEntries(
      PRODUCT_WAREHOUSES.map((warehouse, warehouseIndex) => {
        let quantity =
          ((index + 3) * (warehouseIndex + 5) * 7 + warehouseIndex * 11) % 39;
        if ((index + warehouseIndex) % 17 === 0) quantity = 0;
        else if ((index * 3 + warehouseIndex) % 11 === 0) quantity = 2;
        return [warehouse.key, quantity];
      }),
    );
    const image = productImage(name, index);
    const imageAsset = createDeviceImageAsset({
      deviceId,
      source: image,
      name: `${name} 主图`,
      mimeType: 'image/svg+xml',
      size: new TextEncoder().encode(image).length,
      order: 0,
      kind: 'fixture',
    });
    return {
      id: deviceId,
      name,
      sku: productSku(category, index),
      jkyCode: `JKY-${deviceId.slice(4).padStart(6, '0')}`,
      category,
      deviceAttribute:
        index % 3 === 0 ? '租赁用' : index % 3 === 1 ? '销售用' : '通用类',
      procurementAttribute:
        index % 17 === 0
          ? '暂停采购'
          : index % 7 === 0
            ? '不可采购'
            : '可以采购',
      weight: Number((0.35 + ((index * 37) % 1200) / 100).toFixed(2)),
      sortOrder: index % 6,
      status: (index + 1) % 11 === 0 ? 'inactive' : 'active',
      remark: '',
      rentalPrice,
      taxInclusivePlanPrice,
      retailPriceTax13,
      unitPrice,
      untaxedPlanPrice,
      buyoutFinalPriceTax13,
      dynamicCost,
      compensationPrice,
      pricingFieldAvailability: {
        rentalPrice: true,
        taxInclusivePlanPrice: true,
        retailPriceTax13: true,
        unitPrice: true,
        untaxedPlanPrice: true,
        buyoutFinalPriceTax13: true,
        dynamicCost: true,
        compensationPrice: true,
      },
      image,
      gallery: [image],
      images: [imageAsset],
      primaryImageId: imageAsset.id,
      replacementIds: [],
      stockByWarehouse,
      updatedAt: new Date(
        Date.UTC(2026, 6, 20 + (index % 40), 8, index % 60),
      ).toISOString(),
      updatedAtSource: 'fixture',
    };
  });
}

function buildPlans(): PlanCatalogRecord[] {
  const names = [
    '初创团队直播',
    '品牌电商标准间',
    '双机访谈直播',
    '企业课程录播间',
    '户外活动直播方案',
    '发布会多机位方案',
  ];
  return names.map((name, index) => ({
    id: `PLAN-${String(index + 1).padStart(3, '0')}`,
    name,
    discount: [92, 88, 90, 86, 95, 84][index],
    sortOrder: index,
    image: productImage(name, index + 60),
    cases: [`${name}案例图 1`],
  }));
}

const OPTION_CATEGORIES: OptionCategoryRecord[] = [
  { id: '1', title: '客户来源', name: 'customer_source', sortOrder: 0 },
  { id: '2', title: '交付城市', name: 'delivery_city', sortOrder: 1 },
  { id: '3', title: '付款方式', name: 'pay_mode', sortOrder: 2 },
  { id: '4', title: '服务等级', name: 'service_level', sortOrder: 3 },
];

function buildOptions(): OptionCatalogRecord[] {
  const values: Record<string, string[]> = {
    '1': ['老客户转介绍', '线上咨询', '渠道合作', '展会线索'],
    '2': ['深圳', '广州', '上海', '北京', '杭州'],
    '3': ['全款', '定金+尾款', '月结', '分期'],
    '4': ['标准', '优先', '驻场', '远程'],
  };
  let sequence = 1;
  return OPTION_CATEGORIES.flatMap((category) =>
    values[category.id].map((title, sortOrder) => ({
      id: String(sequence++),
      categoryId: category.id,
      title,
      sortOrder,
    })),
  );
}

function buildPackages(plans: PlanCatalogRecord[]): PackageCatalogRecord[] {
  const names = [
    '轻量单机直播套装',
    '室内课程录制套装',
    '电商绿幕直播套装',
    '双机访谈直播套装',
    '企业课程录播套装',
    '户外活动直播套装',
    '发布会多机位套装',
    '品牌电商标准间套装',
  ];
  const deviceGroups = [
    ['DEV-001', 'DEV-007', 'DEV-019'],
    ['DEV-002', 'DEV-008', 'DEV-013'],
    ['DEV-003', 'DEV-015', 'DEV-020'],
    ['DEV-003', 'DEV-006', 'DEV-020'],
    ['DEV-004', 'DEV-016', 'DEV-025'],
    ['DEV-004', 'DEV-009', 'DEV-019'],
    ['DEV-006', 'DEV-026', 'DEV-031'],
    ['DEV-001', 'DEV-013', 'DEV-020'],
  ];
  const relationPrices = [
    [260, 180, 150],
    [220, 175, 210],
    [45, 90, 145],
    [58, 135, 168],
    [310, 195, 420],
    [335, 205, 155],
    [760, 480, 690],
    [275, 240, 172],
  ];
  return names.map((name, index) => {
    const image = productImage(name, index + 50);
    return {
      id: `PKG-${String(index + 1).padStart(3, '0')}`,
      name,
      planIds: [plans[index % plans.length].id],
      sortOrder: index + 1,
      productLinks: deviceGroups[index].map((deviceId, sortOrder) => ({
        relationId: `PKG-REL-${String(index + 1).padStart(3, '0')}-${String(sortOrder + 1).padStart(2, '0')}`,
        packageId: `PKG-${String(index + 1).padStart(3, '0')}`,
        deviceId,
        quantity: 1,
        sortOrder,
        packageItemPrice: relationPrices[index][sortOrder],
      })),
      image,
      lightImage: productImage(`${name} 细节`, index + 70),
    };
  });
}

function buildCustomers(): CustomerCatalogRecord[] {
  const productCustomers = [
    ['CUST-1026', '云端直播', '刘洋', '13863487188', '陈静'],
    ['CUST-1025', '星辉传媒', '陈静', '13887754777', '李娜'],
    ['CUST-1024', '锐驰科技(深圳)', '吴昊', '13831023390', '王芳'],
    ['CUST-1023', '光合文化', '李娜', '13894951621', '陈经理'],
    ['CUST-1022', '云端直播', '王芳', '13879681335', '张伟'],
    ['CUST-1021', '天成文化', '赵敏', '13820233159', '孙总监'],
    ['CUST-1020', '蓝海科技(深圳)', '杨帆', '13816282632', '李娜'],
    ['CUST-1019', '品诺传播', '吴昊', '13872295820', '徐店长(人人租)'],
    ['CUST-1018', '鼎盛贸易', '赵敏', '13828255863', '陈静'],
    ['CUST-1017', '锐驰科技', '李娜', '13870289038', '刘洋'],
    ['CUST-1016', '星辉传媒(深圳)', '赵敏', '13824349528', '张伟'],
    ['CUST-1015', '蓝海科技', '杨帆', '13834752491', '陈静'],
    ['CUST-1014', '云端直播', '刘洋', '13870659974', '李娜'],
    ['CUST-1013', '品诺传播', '孙悦', '13833263548', '刘洋'],
    ['CUST-1012', '锐驰科技(深圳)', '黄磊', '13860415177', '陈经理'],
    ['CUST-1011', '鼎盛贸易', '吴昊', '13854080764', '刘洋'],
    ['CUST-1010', '汇通国际', '赵敏', '13853666357', '张伟'],
    ['CUST-1009', '悦动视觉', '周琳', '13861204531', '陈经理'],
    ['CUST-1008', '光合文化(广州)', '陈静', '13870923318', '李娜'],
    ['CUST-1007', '天成文化', '杨帆', '13844190726', '刘洋'],
    ['CUST-1006', '蓝海科技', '黄磊', '13855623819', '王芳'],
    ['CUST-1005', '远大商贸', '赵敏', '13866731240', '陈静'],
    ['CUST-1004', '星辉传媒(上海)', '孙悦', '13877842135', '李娜'],
    ['CUST-1003', '新锐电商', '刘洋', '13888953024', '张伟'],
    ['CUST-1002', '云端直播', '陈静', '13899064123', '王芳'],
    ['CUST-1001', '品诺传播', '吴昊', '13810175234', '陈经理'],
  ] as const;
  const managerUserIds: Record<string, string> = {
    陈经理: 'U-003',
    刘洋: 'U-007',
    张伟: 'U-004',
    李娜: 'U-005',
    王芳: 'U-006',
    陈静: 'U-009',
    孙总监: 'U-010',
    '徐店长(人人租)': 'U-011',
  };
  return productCustomers.map((customer, index) => ({
    id: `CUSTOMER-${String(index + 1).padStart(3, '0')}`,
    customerNo: customer[0],
    name: customer[1],
    contact: customer[2],
    phone: customer[3],
    manager: customer[4],
    managerUserId: managerUserIds[customer[4]],
    status: 'active',
  }));
}

function buildUsers(): UserCatalogRecord[] {
  const roleIds: Record<CpqRole, string> = {
    super_admin: 'ROLE-SUPER-ADMIN',
    admin: 'ROLE-ADMIN',
    manager: 'ROLE-MANAGER',
    sales: 'ROLE-SALES',
    tracker: 'ROLE-TRACKER',
    exec: 'ROLE-EXEC',
    ry_sales: 'ROLE-RY',
  };
  const seeds: Omit<UserCatalogRecord, 'password' | 'status'>[] = [
    {
      id: 'U-000',
      username: 'superadmin',
      email: 'superadmin@demo.com',
      displayName: '超级管理员',
      department: '总部',
      role: 'super_admin',
    },
    {
      id: 'U-001',
      username: 'admin',
      email: 'admin@demo.com',
      displayName: '管理员',
      department: '总部',
      role: 'admin',
    },
    {
      id: 'U-002',
      username: 'manager01',
      email: 'manager01@demo.com',
      displayName: '刘经理',
      department: '一店',
      role: 'manager',
    },
    {
      id: 'U-003',
      username: 'manager02',
      email: 'manager02@demo.com',
      displayName: '陈经理',
      department: '二店',
      role: 'manager',
    },
    {
      id: 'U-004',
      username: 'sales4',
      email: 'sales4@demo.com',
      displayName: '张伟',
      department: '一店',
      role: 'sales',
    },
    {
      id: 'U-005',
      username: 'sales5',
      email: 'sales5@demo.com',
      displayName: '李娜',
      department: '二店',
      role: 'sales',
    },
    {
      id: 'U-006',
      username: 'sales6',
      email: 'sales6@demo.com',
      displayName: '王芳',
      department: '三店',
      role: 'sales',
    },
    {
      id: 'U-007',
      username: 'sales7',
      email: 'sales7@demo.com',
      displayName: '刘洋',
      department: '四店',
      role: 'sales',
    },
    {
      id: 'U-008',
      username: 'tracker01',
      email: 'tracker01@demo.com',
      displayName: '吴跟单',
      department: '一店',
      role: 'tracker',
    },
    {
      id: 'U-009',
      username: 'sales8',
      email: 'sales8@demo.com',
      displayName: '陈静',
      department: '一店',
      role: 'sales',
    },
    {
      id: 'U-010',
      username: 'exec01',
      email: 'exec01@demo.com',
      displayName: '孙总监',
      department: '一店',
      role: 'exec',
    },
    {
      id: 'U-011',
      username: 'rysales01',
      email: 'rysales01@demo.com',
      displayName: '徐店长(人人租)',
      department: '三店',
      role: 'ry_sales',
    },
  ];
  return seeds.map((user, index) => ({
    ...user,
    roleId: roleIds[user.role],
    password: 'ant.design',
    status: index === 8 ? 'disabled' : 'active',
  }));
}

function buildRoles(): RoleCatalogRecord[] {
  const all = [
    '报价查看',
    '报价编辑',
    '目录维护',
    '库存维护',
    '用户管理',
    '系统设置',
  ];
  return [
    {
      id: 'ROLE-SUPER-ADMIN',
      key: 'super_admin',
      label: '超级管理员',
      description: '平台治理；独占用户、角色、权限发布、字段治理和系统设置',
      dataScope: '全部数据',
      permissions: all,
      status: 'active',
    },
    {
      id: 'ROLE-ADMIN',
      key: 'admin',
      label: '管理员',
      description: '全公司业务管理；不含用户、角色、权限发布和系统设置',
      dataScope: '全部数据',
      permissions: all.slice(0, 4),
      status: 'active',
    },
    {
      id: 'ROLE-MANAGER',
      key: 'manager',
      label: '客户部/商务部经理',
      description: '部门经理',
      dataScope: '本部门数据',
      permissions: all.slice(0, 4),
      status: 'active',
    },
    {
      id: 'ROLE-SALES',
      key: 'sales',
      label: '客户部/商务部销售',
      description: '客户经理',
      dataScope: '本人数据',
      permissions: all.slice(0, 2),
      status: 'active',
    },
    {
      id: 'ROLE-TRACKER',
      key: 'tracker',
      label: '跟单员',
      description: '报价跟进',
      dataScope: '本部门数据',
      permissions: ['报价查看', '库存维护'],
      status: 'active',
    },
    {
      id: 'ROLE-EXEC',
      key: 'exec',
      label: '管理层',
      description: '经营数据只读',
      dataScope: '全部数据',
      permissions: ['报价查看'],
      status: 'active',
    },
    {
      id: 'ROLE-RY',
      key: 'ry_sales',
      label: '人人租销售',
      description: '人人租销售专区',
      dataScope: '本人数据',
      permissions: ['报价查看', '报价编辑'],
      status: 'active',
    },
  ];
}

function buildSettings(): Record<string, SettingsFieldRecord[]> {
  return {
    产品表格字段: [
      {
        key: 'rental_price',
        title: '租金',
        type: 'number',
        defaultValue: '',
        enabled: true,
      },
      {
        key: 'tax_inclusive_plan_price',
        title: '最终买断价',
        type: 'number',
        defaultValue: '',
        enabled: true,
      },
      {
        key: 'device_unit_price',
        title: '单价',
        type: 'number',
        defaultValue: '',
        enabled: true,
      },
      {
        key: 'device_compensation_price',
        title: '赔付价格',
        type: 'number',
        defaultValue: '',
        enabled: true,
      },
      {
        key: 'fields.good_price',
        title: '套餐单品价',
        type: 'number',
        defaultValue: '',
        enabled: true,
      },
    ],
    推单自定义表单: [
      {
        key: 'CusName',
        title: '客户名称',
        type: 'text',
        defaultValue: '',
        enabled: true,
      },
      {
        key: 'contact',
        title: '联系人',
        type: 'text',
        defaultValue: '',
        enabled: true,
      },
      {
        key: 'phone',
        title: '联系电话',
        type: 'text',
        defaultValue: '',
        enabled: true,
      },
      {
        key: 'customer_source',
        title: '客户来源',
        type: 'select',
        defaultValue: '',
        enabled: true,
      },
    ],
  };
}

/** Disposable LocalStorage Mock seed for the independent domain resource. */
function buildDepartmentCompanyMappings(): DepartmentCompanyMappingRecord[] {
  return [
    ['一店', '常青堂科技有限公司'],
    ['二店', '乙希贸易有限公司'],
    ['三店', '租播科技有限公司'],
    ['四店', '乙希信息技术有限公司'],
  ].map(([departmentName, companyName]) => {
    const departmentId = disposableDepartmentId(departmentName);
    return {
      id: departmentCompanyMappingId(departmentId),
      departmentId,
      departmentName,
      companyId: disposableCompanyId(companyName),
      companyName,
      status: 'active',
      resourceVersion: 'local-1',
      updatedAt: '2026-08-01T00:00:00.000Z',
      origin: 'fixture',
    };
  });
}

export function createProductCatalog(): BusinessCatalog {
  const plans = buildPlans();
  const roles = buildRoles();
  const quoteFieldDefinitions = createQuoteFieldDefinitions();
  const packages = buildPackages(plans);
  return {
    domainSchemaVersion: 3,
    businessModes: createBusinessModeCatalog(),
    warehouses: structuredClone(PRODUCT_WAREHOUSES),
    devices: buildDevices(),
    packages,
    plans,
    optionCategories: structuredClone(OPTION_CATEGORIES),
    options: buildOptions(),
    customers: buildCustomers(),
    users: buildUsers(),
    roles,
    settings: buildSettings(),
    departmentCompanyMappings: buildDepartmentCompanyMappings(),
    quoteFieldDefinitions,
    quoteFieldPool: createQuoteFieldPool(),
    quoteFieldCombinations: createQuoteFieldCombinations(),
    shareFieldCombinations: createShareFieldCombinations(),
    quoteFieldPresets: createQuoteFieldPresets(),
    permissionGovernance: createInitialPermissionGovernance(
      roles,
      quoteFieldDefinitions,
    ),
  };
}
